import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff, KeyRound, Loader2, Lock, LogOut, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AuroraBackdrop } from '../components/AuroraBackdrop';
import { BrandMark } from '../components/BrandMark';
import { ThemeToggle } from '../components/ThemeToggle';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useAuth } from '../contexts/AuthContext';
import { initialsOf } from '../utils/format';
import { accountProfile } from '../utils/profile';
import { scorePassword } from '../utils/security';
const toneStyles: Record<string, string> = {
  weak: 'bg-rose-500',
  fair: 'bg-amber-500',
  good: 'bg-sky-500',
  strong: 'bg-emerald-500'
};
export function Security() {
  const navigate = useNavigate();
  const {
    user,
    hasMasterPassword,
    createMasterPassword,
    unlockWithMasterPassword,
    logout
  } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const profile = accountProfile(user);
  const strength = scorePassword(password);
  const creating = !hasMasterPassword;
  const signOutAndLeave = async () => {
    try {
      await logout();
    } catch {
      /* leaving anyway */
    }
    navigate('/', {
      replace: true
    });
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (creating) {
      if (password.length < 6) {
        setError('Use at least 6 characters for your master password.');
        return;
      }
      if (password !== confirm) {
        setError('The two passwords do not match. Please re-type them.');
        return;
      }
      setBusy(true);
      try {
        await createMasterPassword(password);
        toast.success('Account secured', {
          description: 'Stored on your Supabase account — usable on any device.'
        });
        navigate('/dashboard', {
          replace: true
        });
      } catch (createError) {
        setError(createError instanceof Error ? createError.message : 'Could not save your master password.');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!password) {
      setError('Enter your master password to continue.');
      return;
    }
    setBusy(true);
    try {
      const ok = await unlockWithMasterPassword(password);
      if (!ok) {
        setError('That master password is incorrect.');
        return;
      }
      toast.success('Welcome back');
      navigate('/dashboard', {
        replace: true
      });
    } finally {
      setBusy(false);
    }
  };
  return <div className="relative flex min-h-screen w-full flex-col" data-id="element-585" data-mp="204zc1yv0uai9">
      <AuroraBackdrop intensity="marketing" data-id="element-586" data-mp="205iqv6pdkis2" />

      <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-5 py-6" data-id="element-587" data-mp="fn80ou5gpsgv">
        <BrandMark size="sm" data-id="element-588" data-mp="kl9sc3gyf03y" />
        <div className="flex items-center gap-2" data-id="element-589" data-mp="kkghorevjqgj">
          <ThemeToggle data-id="element-590" data-mp="ond4yvfd0aqt" />
          <button type="button" onClick={signOutAndLeave} className="glass-flat inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground" data-id="element-591" data-mp="on4v7cej9lju">
            <LogOut className="h-3.5 w-3.5" data-id="element-592" data-mp="xsapgdr9r28" />
            Log out
          </button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-5 pb-16" data-id="element-593" data-mp="5x57otycceij">
        <motion.div initial={{
        opacity: 0,
        y: 18,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.45,
        ease: 'easeOut'
      }} className="glass-strong rounded-3xl p-7 sm:p-9" data-id="element-594" data-mp="anbvirl13uzq">
          <div className="flex items-center gap-3" data-id="element-595" data-mp="3eb441wp861wk">
            <div className="relative" data-id="element-596" data-mp="1ktg9h5w1pd7i">
              {profile.avatarUrl ? <img src={profile.avatarUrl} alt={profile.name} className="h-12 w-12 rounded-2xl border border-[var(--glass-border)] object-cover" data-id="element-597" data-mp="1fuv8v5cvsrij" /> : <span className="grid h-12 w-12 place-items-center rounded-2xl brand-gradient text-sm font-semibold text-white" data-id="element-598" data-mp="1ouibtvl9e5dp">
                  {initialsOf(profile.name)}
                </span>}
              <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white" data-id="element-599" data-mp="1ykl39rcqfb9x">
                <ShieldCheck className="h-3 w-3" data-id="element-600" data-mp="2cj1gm153o011" />
              </span>
            </div>
            <div className="min-w-0" data-id="element-601" data-mp="28kbunk5zxa5s">
              <p className="truncate text-sm font-semibold text-foreground" data-id="element-602" data-mp="i6uu466r9wtf">{profile.name}</p>
              <p className="truncate text-xs text-muted-foreground" data-id="element-603" data-mp="e5stpm13repv">{profile.email}</p>
            </div>
          </div>

          <div className="mt-7" data-id="element-604" data-mp="41cemuvg61n4">
            <span className="glass-flat inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-605" data-mp="4249g3h7zvcc">
              Step 2 of 2 — Security layer
            </span>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-foreground" data-id="element-606" data-mp="3vga3shder4vb">
              {creating ? 'Create your master password' : 'Enter your master password'}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground" data-id="element-607" data-mp="3glc8pgqvk1aj">
              {creating ? 'This is a new Student Recorder account. Type a master password twice — it is stored on your Supabase account, so the same login unlocks the same records anywhere.' : 'This account already has a Student Recorder profile. Unlock it with your master password to reach your records.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate data-id="element-608" data-mp="1aqaykfnqz9we">
            <div className="space-y-2" data-id="element-609" data-mp="18b4gg4tbdxbf">
              <Label htmlFor="master-password" data-id="element-610" data-mp="18c5t2dpzarv9">Master password</Label>
              <div className="relative" data-id="element-611" data-mp="1499eqz7h1hx4">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" data-id="element-612" data-mp="1487uq58h9l39" />
                <Input id="master-password" type={reveal ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} placeholder="At least 6 characters" autoComplete={creating ? 'new-password' : 'current-password'} aria-invalid={Boolean(error) || undefined} className="h-11 rounded-xl pl-9 pr-10" data-id="element-613" data-mp="zciyobvo2mbp" />
                <button type="button" onClick={() => setReveal(value => !value)} aria-label={reveal ? 'Hide password' : 'Show password'} className="absolute right-2.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:text-foreground" data-id="element-614" data-mp="1d8mqjyj2iil0">
                  {reveal ? <EyeOff className="h-4 w-4" data-id="element-615" data-mp="2i9mbnmlszzzl" /> : <Eye className="h-4 w-4" data-id="element-616" data-mp="2n8qk2b0wltov" />}
                </button>
              </div>

              {creating && password.length > 0 && <div className="flex items-center gap-2 pt-1" data-id="element-617" data-mp="2s8bi8j891y0j">
                  <div className="flex flex-1 gap-1" data-id="element-618" data-mp="2w9oqezytedam">
                    {[1, 2, 3, 4].map(step => <span key={step} className={`h-1.5 flex-1 rounded-full transition-colors ${step <= strength.score ? toneStyles[strength.tone] : 'bg-[var(--glass-hairline)]'}`} data-id="element-619" data-mp="317qbtrjpqop9" />)}
                  </div>
                  <span className="w-14 text-right text-xs font-medium text-muted-foreground" data-id="element-620" data-mp="3fv1j3h8v3mjk">{strength.label}</span>
                </div>}
            </div>

            {creating && <div className="space-y-2" data-id="element-621" data-mp="96d9sa4jk7c4">
                <Label htmlFor="master-password-confirm" data-id="element-622" data-mp="538aegfrclrp">Confirm master password</Label>
                <div className="relative" data-id="element-623" data-mp="j27vje42aij4">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" data-id="element-624" data-mp="j154n66qog37" />
                  <Input id="master-password-confirm" type={reveal ? 'text' : 'password'} value={confirm} onChange={event => setConfirm(event.target.value)} placeholder="Type it again" autoComplete="new-password" aria-invalid={Boolean(error) || undefined} className="h-11 rounded-xl pl-9" data-id="element-625" data-mp="e1ajzkdk4mql" />
                </div>
                {confirm.length > 0 && confirm !== password && <p className="text-xs font-medium text-destructive" data-id="element-626" data-mp="2yskmd325qr7n">Passwords do not match yet.</p>}
              </div>}

            {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive" data-id="element-627" data-mp="1j2tlulu7imhm">
                {error}
              </p>}

            <Button type="submit" disabled={busy} className="h-12 w-full rounded-2xl brand-gradient text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 hover:opacity-95" data-id="element-628" data-mp="19cgpzxgxz6w6">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-629" data-mp="21zur87roaom3" /> : <ArrowRight className="mr-2 h-4 w-4" data-id="element-630" data-mp="1o1d8kogmk2b1" />}
              {busy ? creating ? 'Saving…' : 'Unlocking…' : creating ? 'Create & enter dashboard' : 'Unlock dashboard'}
            </Button>
          </form>

          <button type="button" onClick={signOutAndLeave} className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground" data-id="element-631" data-mp="hl1302bh3i5f">
            <LogOut className="h-3.5 w-3.5" data-id="element-632" data-mp="3muofc33qy9uj" />
            Log out / use a different Google account
          </button>
        </motion.div>

        <p className="mt-6 text-center text-xs text-muted-foreground" data-id="element-633" data-mp="392qiorr826f9">
          Account ID <span className="font-mono" data-id="element-634" data-mp="3e1jp6083cx5x">{profile.id.slice(0, 12) || '—'}…</span> — every account keeps its own
          isolated rows.
        </p>
      </main>
    </div>;
}